import { useState, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';

/**
 * Hook to protect export/download actions with admin password verification.
 * Returns: { requestExport, AdminPasswordModal }
 * 
 * Usage:
 *   const { requestExport, AdminPasswordModal } = useProtectedExport();
 *   // When user clicks export button:
 *   requestExport(() => { actualExportFunction(); });
 *   // In JSX:
 *   <AdminPasswordModal />
 */
const useProtectedExport = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const requestExport = useCallback((exportFn) => {
    setPendingAction(() => exportFn);
    setPassword('');
    setShowDialog(true);
  }, []);

  const handleVerify = useCallback(async () => {
    if (!password.trim()) {
      toast.error('Please enter admin password');
      return;
    }
    setVerifying(true);
    try {
      await api.post('/auth/verify-admin-password', { password });
      setShowDialog(false);
      setPassword('');
      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect admin password');
    } finally {
      setVerifying(false);
    }
  }, [password, pendingAction]);

  const handleClose = useCallback(() => {
    setShowDialog(false);
    setPassword('');
    setPendingAction(null);
  }, []);

  return {
    requestExport,
    showDialog,
    password,
    setPassword,
    verifying,
    handleVerify,
    handleClose
  };
};

export default useProtectedExport;
